import { ScrollTop } from "@/components/commons/ScrollTop";
import { AppDrawer } from "@/components/layout/elements/AppDrawer";
import { AppHeader } from "@/components/layout/elements/AppHeader";
import { useMeAutoRefresh } from "@/features/auth/hooks/useMeAutoRefresh";
import { withAuth } from "@/hoc/withAuth";
import { useVisible } from "@/hooks/useVisible";
import { useMaintenance } from "@/providers/MaintenanceProvider";
import { InitSetupProvider } from "@/providers/InitSetupProvider";
import { background } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { FCC } from "@/types/common";
import { MAINTENANCE_MODE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Box, Stack, styled } from "@mui/material";
import { useMemo } from "react";
import { Outlet } from "react-router-dom";
import { SocketProvider } from "@/features/chat/providers/SocketProvider";
import { ChatProvider } from "@/features/chat/providers/ChatProvider";
import { Chat } from "@/features/chat/components/Chat";

export const MatchLayout: FCC = ({ children }) => {
  const showDrawer = useVisible();
  const { data } = useMaintenance();
  useMeAutoRefresh();

  const isMaintenance = useMemo(() => {
    if (!data?.maintenanceMode || data.maintenanceMode === MAINTENANCE_MODE.SYSTEM_NORMAL_OPERATION) {
      return false;
    }

    const now = dayjs();
    const start = data.modeStartDateTime ? dayjs(data.modeStartDateTime) : null;
    const end = data.modeEndDateTime ? dayjs(data.modeEndDateTime) : null;

    return (!start || now.isSameOrAfter(start)) && (!end || now.isSameOrBefore(end));
  }, [data]);

  return (
    <InitSetupProvider>
      <ScrollTop />
      <SocketProvider>
        <AppHeader isOpenDrawer={showDrawer.visible} toggleDrawer={showDrawer.toggle} />
        <StackWrapper direction="row">
          <AppDrawer isOpenDrawer={showDrawer.visible} toggleDrawer={showDrawer.toggle} />
          <ChildrenWrapper onClick={showDrawer.close}>
            <Outlet />
            {children}
          </ChildrenWrapper>
        </StackWrapper>
        {!isMaintenance && (
          <ChatProvider>
            <Chat />
          </ChatProvider>
        )}
      </SocketProvider>
    </InitSetupProvider>
  );
};

export const MatchLayoutWithAuth = withAuth(MatchLayout);

const ChildrenWrapper = styled(Box)`
  padding: ${spacing.md};
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  margin: 0 auto;
`;

const StackWrapper = styled(Stack)`
  min-height: calc(100vh - 57px);
  background-color: ${background.primary};
`;
